import { TreeData } from "@/utils/tree-generation/tree-data";
import { GUID } from "@faro-lotv/foundation";

/**
 * @param nodes The tree nodes to collect the scans from
 * @param invalidIds Clusters without scans that can be ordered
 * @returns the ids of the scans below the nodes
 */
export function collectScanIds(
  nodes: TreeData[],
  invalidIds: Set<GUID>,
): GUID[] {
  return nodes.flatMap((node) => {
    if (invalidIds.has(node.id)) return [];
    return node.children
      ? collectScanIds(node.children, invalidIds)
      : [node.id];
  });
}

/**
 * Checkbox selection of the scans sent to IPX: every scan is checked while the whole point cloud is selected,
 * so checking or unchecking an item adds it to or removes it from what is checked.
 * The selection is never empty: an empty list means the whole point cloud.
 *
 * @param selectedIds The selected scans, empty for the whole point cloud
 * @param allScanIds All the scans of the point cloud
 * @param clickedScanIds The scans of the clicked item: a scan, a cluster or the point cloud
 * @returns the new selected scans, empty for the whole point cloud
 */
export function toggleIpxScanCheckbox(
  selectedIds: GUID[],
  allScanIds: GUID[],
  clickedScanIds: GUID[],
): GUID[] {
  const next = new Set(selectedIds.length ? selectedIds : allScanIds);
  const isRemoving = clickedScanIds.every((id) => next.has(id));
  for (const id of clickedScanIds) {
    if (isRemoving) next.delete(id);
    else next.add(id);
  }
  // The last checked item can't be unchecked, as there would be nothing to send
  if (!next.size) return selectedIds;
  return next.size === allScanIds.length
    ? []
    : allScanIds.filter((id) => next.has(id));
}
